import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Stack,
  Chip,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Switch,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getFirestore, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import firebaseApp from '../utils/firebaseConfig';
import { PERMISSIONS, hasPermission } from '../protectedRoute';

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'type', label: 'Type', alignRight: false },
  { id: 'durationMinutes', label: 'Duration', alignRight: false },
  { id: 'priceMemberCents', label: 'Member Price', alignRight: false },
  { id: 'priceNonMemberCents', label: 'Non-Member Price', alignRight: false },
  { id: 'active', label: 'Active', alignRight: false },
  { id: '', label: '', alignRight: false },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_item) => _item.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

const db = getFirestore(firebaseApp);

const typeColors = {
  lane: 'error',
  course: 'info',
  class: 'warning',
  event: 'success',
};

export default function ReservationCategoriesPage() {
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('sortOrder');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [categories, setCategories] = useState([]);
  const [row, setRow] = useState({});
  const [tableLoading, setTableLoading] = useState(true);
  const navigate = useNavigate();
  const canEdit = hasPermission(PERMISSIONS.EDIT_CATEGORIES);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setTableLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'reservation_categories'));
      const data = snapshot.docs.map((d) => ({ ...d.data(), id: d.id }));
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
    setTableLoading(false);
  };

  const handleOpenMenu = (event, row) => {
    setOpen(event.currentTarget);
    setRow(row);
  };

  const handleCloseMenu = () => setOpen(null);

  const handleEditClick = () => {
    navigate(`information?id=${row.id}`);
    handleCloseMenu();
  };

  const handleDeleteClick = async () => {
    if (!window.confirm(`Are you sure you want to delete "${row.name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'reservation_categories', row.id));
      setCategories(categories.filter((c) => c.id !== row.id));
      alert('Category deleted successfully');
    } catch (error) {
      alert('Error deleting category');
    }
    handleCloseMenu();
  };

  const handleToggleActive = async (item) => {
    try {
      await updateDoc(doc(db, 'reservation_categories', item.id), { active: !item.active });
      setCategories(categories.map((c) => (c.id === item.id ? { ...c, active: !c.active } : c)));
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

  const filteredCategories = applySortFilter(categories, getComparator(order, orderBy), filterName);
  const isNotFound = !filteredCategories.length && !!filterName;

  return (
    <>
      <Helmet>
        <title>Reservation Categories | BBTC Dashboard</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Reservation Categories
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => navigate('information')}
            >
              New Category
            </Button>
          )}
        </Stack>

        <Card>
          <UserListToolbar filterName={filterName} onFilterName={(e) => { setPage(0); setFilterName(e.target.value); }} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={(e, prop) => {
                    setOrder(orderBy === prop && order === 'asc' ? 'desc' : 'asc');
                    setOrderBy(prop);
                  }}
                />
                <TableBody>
                  {tableLoading && <TableSkeleton rows={5} columns={7} />}
                  {!tableLoading && filteredCategories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow hover key={row.id} tabIndex={-1}>
                      <TableCell padding="checkbox" />
                      <TableCell component="th" scope="row">
                        <Typography variant="subtitle2" noWrap>{row.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.type} color={typeColors[row.type] || 'default'} size="small" />
                      </TableCell>
                      <TableCell>{row.durationMinutes} min</TableCell>
                      <TableCell>{formatPrice(row.priceMemberCents || 0)}</TableCell>
                      <TableCell>{formatPrice(row.priceNonMemberCents || 0)}</TableCell>
                      <TableCell>
                        <Switch checked={row.active || false} disabled={!canEdit} onChange={() => handleToggleActive(row)} />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, row)}>
                          <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!tableLoading && filteredCategories.length === 0 && (
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                        <Typography variant="body2">{isNotFound ? `No results for "${filterName}"` : 'No categories found'}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredCategories.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 1, width: 140 } }}
      >
        <MenuItem onClick={handleEditClick}>
          <Iconify icon={canEdit ? 'eva:edit-fill' : 'eva:eye-fill'} sx={{ mr: 2 }} /> {canEdit ? 'Edit' : 'Ver'}
        </MenuItem>
        {canEdit && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} /> Delete
          </MenuItem>
        )}
      </Popover>
    </>
  );
}
